/**
 * The things a ranged attack sends across the air, and how each is drawn in
 * flight. Asked for on 2026-09-27: "make sure the projectile that is shown
 * when something uses a ranged attack matches the attack. rocks, arrows and
 * bolts for example."
 *
 * Until then every shot was drawn as an arrow — a skeleton's arrow, a
 * player's thrown rock and anything else that was loosed — because the shot
 * effect said only who had shot and where it landed. It now says what was
 * loosed as well: the server puts the item id of the thing flying in the
 * effect's value (the arrow off a bow, the bolt off a crossbow, the rock
 * itself when a rock is thrown), and the client turns that id into a drawing
 * here.
 *
 * HOW AN ID BECOMES A DRAWING, and why it is done this way. The client knows
 * item ids only through the definitions the server sends when you log in;
 * it keeps no table of its own, and item ids are only ever appended to, so
 * a table here would be a second copy of the server's numbering waiting to
 * fall out of step. Nor is the drawing chosen by the item's name, which is
 * prose a player reads and is free to be reworded. Instead the server marks
 * the items that fly with a word of their own, `missile` on the definition
 * ("arrow", "bolt", "rock"), and this module draws by that word. The id on
 * the wire is looked up in the definitions the client already has, and the
 * word on the definition picks the picture: a new item that flies is one
 * word on the server and no change here unless it wants a new picture, and
 * a word this module does not know falls back to the arrow, which is what
 * every shot was drawn as before.
 *
 * It is a module of its own, apart from the renderer, so that the drawings
 * can be run under node against a recording canvas and told apart
 * (TestEachMissileIsDrawnAsItself); render.ts only calls in.
 */

import type { ItemDef } from "./types";

type Ctx = CanvasRenderingContext2D;

/**
 * What is in flight, for the three things a weapon sends, the hill
 * troll's boulder and the black rod's fireball (2026-09-28: "give hill trolls a big boulder (scaled up
 * rock) in one hand, and let them have a ranged attack if they're not close
 * to an enemy").
 */
export type Missile = "arrow" | "bolt" | "rock" | "boulder" | "fireball" | "bluefire";

const MISSILES: readonly Missile[] = ["arrow", "bolt", "rock", "boulder", "fireball", "bluefire"];

/**
 * The drawing for whatever the shot effect's value names: the definition's
 * own word if it is one this module draws, and the arrow otherwise — an older
 * server that sent no value, or an item nobody has drawn a flight for yet.
 */
export function missileKind(def: ItemDef | undefined): Missile {
  const m = def?.missile;
  return m && (MISSILES as readonly string[]).includes(m) ? (m as Missile) : "arrow";
}

/** How long an arrow takes to cross to its target, whatever the distance. */
export const ARROW_MS = 220;

/**
 * A crossbow bolt is quicker than an arrow and flies flat: a short, heavy
 * shaft off a steel prod. Faster by about a quarter, which is enough for the
 * eye to see that it is a different thing and not so fast it is missed.
 */
export const QUARREL_MS = 160;

/**
 * A thrown rock is slower than either, and lobbed rather than shot: it is a
 * throw, and it should read as one.
 */
export const ROCK_MS = 300;

/** A boulder is heavier again, and hangs in the air longer on a higher arc. */
export const BOULDER_MS = 420;

/**
 * The black rod's little fireball (2026-10-22: "make the black rod ranged
 * with a range of 2. make it shoot a little fireball"): a touch slower than
 * an arrow, so the eye catches the flame crossing.
 */
export const FIREBALL_MS = 260;

/** How long each missile is drawn crossing to its target. */
export function missileMs(kind: Missile): number {
  return kind === "bolt" ? QUARREL_MS : kind === "rock" ? ROCK_MS : kind === "boulder" ? BOULDER_MS : kind === "fireball" || kind === "bluefire" ? FIREBALL_MS : ARROW_MS;
}

/**
 * Draw one missile with its head at (x, y), travelling along the unit vector
 * (ux, uy), t of the way through its flight (0 to 1). The caller has worked
 * out where the head is and how dark it is there; this only paints.
 * `travelled` is how far the head has come, so that a shaft is never drawn
 * reaching back past whoever loosed it.
 */
export function drawMissile(ctx: Ctx, kind: Missile, x: number, y: number, ux: number, uy: number, t: number, travelled = Infinity) {
  if (kind === "bolt") {
    // A crossbow bolt: shorter and thicker than an arrow, dark wood, and an
    // iron head that is a head and not a pale dot — squat and pointed, the
    // shape that says a crossbow. It flies flat and straight; nothing about
    // it wobbles.
    const tail = Math.min(7, travelled);
    ctx.lineCap = "butt";
    ctx.strokeStyle = "#3e2a1a";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(x - ux * tail, y - uy * tail);
    ctx.lineTo(x - ux * 1.5, y - uy * 1.5);
    ctx.stroke();
    // The vanes at the back, two short flicks of leather either side.
    const px = -uy, py = ux;
    ctx.strokeStyle = "#7a5634";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - ux * tail + px * 2, y - uy * tail + py * 2);
    ctx.lineTo(x - ux * (tail - 2.5), y - uy * (tail - 2.5));
    ctx.lineTo(x - ux * tail - px * 2, y - uy * tail - py * 2);
    ctx.stroke();
    // The iron head: a squat triangle pointing the way it flies.
    ctx.fillStyle = "#8d949c";
    ctx.beginPath();
    ctx.moveTo(x + ux * 2.5, y + uy * 2.5);
    ctx.lineTo(x - ux * 1.5 + px * 2, y - uy * 1.5 + py * 2);
    ctx.lineTo(x - ux * 1.5 - px * 2, y - uy * 1.5 - py * 2);
    ctx.closePath();
    ctx.fill();
    return;
  }
  if (kind === "bluefire") {
    // The Blue Tower's blue fire (2026-11-01), off the Blue Wizard's staff
    // and out of his acolytes' hands: the fireball's shape in a cold blue,
    // a white heart and a tail of fading blue sparks.
    const tail = Math.min(8, travelled);
    const px = -uy, py = ux;
    for (let i = 1; i <= 3; i++) {
      const back = (tail * i) / 3;
      const wob = Math.sin(t * 40 + i * 2) * 0.8;
      ctx.fillStyle = i === 1 ? "rgba(110,170,255,0.85)" : i === 2 ? "rgba(60,110,230,0.55)" : "rgba(30,60,170,0.35)";
      ctx.beginPath();
      ctx.arc(x - ux * back + px * wob, y - uy * back + py * wob, 2.4 - i * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "#3a7aff";
    ctx.beginPath();
    ctx.arc(x, y, 3 + Math.sin(t * 30) * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#e8f4ff";
    ctx.beginPath();
    ctx.arc(x, y, 1.3, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (kind === "fireball") {
    // The black rod's fireball: a little ball of flame, smaller than a
    // geomancer's lava, with a yellow-white heart, an orange skin that
    // flickers as it goes, and a short fading tail of embers behind it.
    // Round and in the fire's colours, with no shaft.
    const tail = Math.min(7, travelled);
    const px = -uy, py = ux;
    for (let i = 1; i <= 3; i++) {
      const back = (tail * i) / 3;
      const wob = Math.sin(t * 40 + i * 2) * 0.8;
      ctx.fillStyle = i === 1 ? "rgba(255,140,40,0.8)" : i === 2 ? "rgba(220,80,20,0.55)" : "rgba(160,40,10,0.35)";
      ctx.beginPath();
      ctx.arc(x - ux * back + px * wob, y - uy * back + py * wob, 2.2 - i * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "#ff6a14";
    ctx.beginPath();
    ctx.arc(x, y, 2.6 + Math.sin(t * 30) * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff2b0";
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (kind === "rock" || kind === "boulder") {
    // A stone, tumbling. No shaft and no head: a small grey lump with its
    // lit facet turning over as it goes, lifted into a low arc because it
    // was thrown rather than loosed. A boulder is the same stone scaled up
    // two and a half times, turning over more slowly and lobbed twice as
    // high, since it is heaved rather than thrown.
    const big = kind === "boulder";
    const size = big ? 2.5 : 1;
    const lift = Math.sin(Math.PI * t) * (big ? 10 : 5);
    const cx = x, cy = y - lift;
    const spin = t * Math.PI * (big ? 2 : 4);
    const pts: [number, number][] = [];
    const r = [3, 2.4, 2.8, 2.2, 2.7];
    for (let i = 0; i < r.length; i++) {
      const a = spin + (i / r.length) * Math.PI * 2;
      pts.push([cx + Math.cos(a) * r[i] * size, cy + Math.sin(a) * r[i] * size]);
    }
    ctx.fillStyle = "#7d7a74";
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (const [px, py] of pts.slice(1)) ctx.lineTo(px, py);
    ctx.closePath();
    ctx.fill();
    // The lit facet, riding round with the spin.
    ctx.fillStyle = "#a29e96";
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(pts[0][0], pts[0][1]);
    ctx.lineTo(pts[1][0], pts[1][1]);
    ctx.closePath();
    ctx.fill();
    return;
  }
  // The arrow, as every shot was drawn before: a short dark shaft with a
  // pale head.
  const tail = Math.min(10, travelled);
  ctx.strokeStyle = "#6b4a2a";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x - ux * tail, y - uy * tail);
  ctx.lineTo(x, y);
  ctx.stroke();
  ctx.fillStyle = "#e8e8e8";
  ctx.beginPath();
  ctx.arc(x, y, 1.5, 0, Math.PI * 2);
  ctx.fill();
}
